/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { classnames } from 'Utils/classnames/classnames.utils';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { Fragment, useState } from 'react';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { getInstallmentPrice } from 'Components/utils/price/price.util';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { truncate } from 'Components/utils/string/string.util';
import { themes } from 'Components/widgets/trip-date-picker/TripDatePicker.themes';
import { t, has } from 'Src/i18n';
import type { PriceOfDay } from 'Store/calendar/calendar.helper';
import 'Store/calendar/calendar.helper';
import { DayPriceDetails } from './DayPriceDetails';
import { DayPriceDoubleSeason } from './DayPriceDoubleSeason';
const COMPACT_MAX_CHARACTERS = 5;
type Props = {
  day: Date;
  duration?: number;
  hasInstalment?: boolean;
  isJoyview?: boolean;
  isLoading: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
  locale: string;
  price: PriceOfDay;
  product?: Product;
  selectedBestPrice?: string | null | undefined;
  selectedDuration?: number;
  selectedRealPrice?: string | null | undefined;
  shouldDisplayDetails: boolean;
  shouldDisplayDoubleSeasonButton: boolean;
  shouldDisplayPrice: boolean;
  shouldDisplaySelectedDepartureDateMessage: boolean;
  shouldDisplaySelectedDepartureDateMessageWithPrice: boolean;
  shouldDisplaySelectedReturningDateMessage: boolean;
  shouldDisplayStatusMessage: boolean;
  theme?: keyof typeof themes;
  withPriceDetails: boolean;
};
export const DayWithPrice = ({
  day,
  duration = 7,
  hasInstalment = false,
  isJoyview,
  isLoading,
  isSelectable = false,
  isSelected = false,
  isHighlighted = false,
  locale,
  price: {
    availability,
    currency,
    departureCity,
    hasTransport,
    isBestPrice,
    lastAvailability,
    price: formattedPriceValue,
    oldPrice,
    priceValue,
    siblingOpen = false,
    unit,
  },
  product,
  selectedBestPrice,
  selectedDuration,
  selectedRealPrice,
  shouldDisplayDetails,
  shouldDisplayDoubleSeasonButton,
  shouldDisplayPrice,
  shouldDisplaySelectedDepartureDateMessage,
  shouldDisplaySelectedDepartureDateMessageWithPrice,
  shouldDisplaySelectedReturningDateMessage,
  shouldDisplayStatusMessage,
  theme,
  withPriceDetails,
}: Props) => {
  const [isMouseOverDoubleSeasonPopin, setIsMouseOverDoubleSeasonPopin] = useState(false);
  const [isVisible, setIsVisibleState] = useState(false);
  const { isDesktop, isTablet } = useResponsive();
  const statusMessageKey = `booking.priceCalendar.priceStatus.${availability}`;
  const {
    thDayCornerMark,
    thDayMessage,
    thDayNumberContainer,
    thDayNumber,
    thDayHighlight,
    thDayOldPrice,
    thDayPrice,
    thStatusMessage,
    thFrom,
    thShowPricePlaceholder,
  } = themes({
    isBestPrice,
    isHighlighted,
    isSelectable,
    isSelected,
    isTablet,
    lastAvailability,
    shouldDisplayDoubleSeasonButton,
    siblingOpen,
  })[theme];
  const setIsVisible = debounce((visible) => {
    setIsVisibleState(visible);
  }, 150);

  const setVisible = () => {
    if (!isVisible) {
      setIsVisible(true);
    }
  };

  const setInvisible = () => {
    setIsVisible(false);
  };

  const getCompactPrice = (rawPrice) => {
    const compactPrice = rawPrice?.replace(/[\s|.,]/gi, '') || '';
    return compactPrice.length <= COMPACT_MAX_CHARACTERS ? (
      compactPrice
    ) : (
      <span className="text-8 md:mx-1 md:px-1 md:text-10">{compactPrice}</span>
    );
  };

  const price = hasInstalment
    ? getInstallmentPrice(
        {
          currency,
          formattedValue: formattedPriceValue,
          value: priceValue,
        },
        locale,
      )
    : formattedPriceValue;
  const selectedPrice = selectedRealPrice || selectedBestPrice;
  const isDateSelectable = ['open', 'noDeparture'].includes(availability);
  return (
    <Fragment>
      <div
        className={classnames({
          'absolute inset-0 z-3 h-full w-full': withPriceDetails || shouldDisplayDoubleSeasonButton,
          'pointer-events-auto': shouldDisplayDoubleSeasonButton,
        })}
        data-testid="price-calendar-date"
        onBlur={() => {
          if (isDesktop) {
            setInvisible();
          }
        }}
        onClick={(e) => {
          if (shouldDisplayDoubleSeasonButton) {
            e.preventDefault();
            e.stopPropagation();

            if (!isDesktop) {
              setVisible();
            }
          }
        }}
        onFocus={() => {
          if (isDesktop) {
            setVisible();
          }
        }}
        onMouseLeave={() => {
          if (isDesktop) {
            setInvisible();
          }
        }}
        onMouseOver={() => {
          if (isDesktop) {
            setVisible();
          }
        }}
        tabIndex={0}
      />
      {siblingOpen && <span className={thDayCornerMark} />}
      {withPriceDetails && (
        <DayPriceDetails
          duration={duration}
          departureCity={departureCity}
          hasTransport={hasTransport}
          isJoyview={isJoyview}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          shouldDisplayDetails={!!price && shouldDisplayDetails}
          day={day}
          unit={unit}
        />
      )}
      {product && siblingOpen && shouldDisplayDoubleSeasonButton && (
        <DayPriceDoubleSeason
          availability={availability}
          product={product}
          isMouseOverDoubleSeasonPopin={isMouseOverDoubleSeasonPopin}
          isVisible={isVisible}
          setInvisible={setInvisible}
          setIsMouseOverDoubleSeasonPopin={setIsMouseOverDoubleSeasonPopin}
          setVisible={setVisible}
          statusMessageKey={statusMessageKey}
        />
      )}
      {(!shouldDisplayDoubleSeasonButton || isTablet) && (
        <div className={thDayNumberContainer}>
          <span className={classnames(thDayNumber)}>{moment(day).format('D')}</span>
          {isHighlighted && <div className={thDayHighlight} />}
        </div>
      )}
      {shouldDisplayStatusMessage &&
        has(statusMessageKey) &&
        !price &&
        !isLoading &&
        (siblingOpen ? (
          shouldDisplayDoubleSeasonButton && (
            <span
              className={classnames(thStatusMessage, {
                'shadow-md': isVisible || isMouseOverDoubleSeasonPopin,
              })}
            >
              {t(statusMessageKey)}
            </span>
          )
        ) : (
          <span className={thStatusMessage}>{t(statusMessageKey)}</span>
        ))}

      {shouldDisplaySelectedDepartureDateMessage && (
        <div className={thDayMessage}>
          {t('booking.priceCalendar.selectedDepartureDateMessage').toUpperCase()}{' '}
          <div className="mt-5">{t('booking.priceCalendar.endDateMessage')}</div>
        </div>
      )}
      {shouldDisplaySelectedDepartureDateMessageWithPrice && (
        <div className={thDayMessage}>
          {t('booking.priceCalendar.selectedDepartureDateMessage').toUpperCase()}
          {selectedPrice && selectedDuration && (
            <div className="flex flex-col">
              <span>{t('price.format.unitTotalPerTripIndicative').toUpperCase()}</span>
              <span
                className="text-12 font-bold leading-tight md:text-16"
                data-testid="selected-date-best-price"
              >
                {theme === 'compact' ? getCompactPrice(selectedPrice) : selectedPrice}
              </span>
            </div>
          )}
        </div>
      )}
      {shouldDisplaySelectedReturningDateMessage && (
        <div className={thDayMessage}>
          {t('booking.priceCalendar.selectedReturningDateMessage').toUpperCase()}
        </div>
      )}
      {isLoading ? (
        <Spinner size="tiny" className="text-black" />
      ) : (
        <Fragment>
          {shouldDisplayPrice && !!price && (
            <div className="flex flex-col text-current" data-testid="price-calendar-date-price">
              {!!price && <span className={thFrom}>{t('price.format.from')}</span>}
              <span data-testid="price" className={thDayPrice}>
                {theme === 'compact' ? getCompactPrice(price) : price}
              </span>
              {price !== oldPrice && <span className={thDayOldPrice}>{oldPrice}</span>}
            </div>
          )}
          {thShowPricePlaceholder && (!shouldDisplayPrice || !price) && (
            <span
              className={classnames(thDayPrice, {
                'opacity-0': !isDateSelectable,
              })}
            >
              {isDateSelectable ? truncate(t('faceting.when.dateSelect'), 6) : <>&nbsp;</>}
            </span>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};
